import {NumberCol, SelectInput, Table, ViewOnly} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {useProjectId, useProjectUnitSystem} from "../../shopdrawing/ProjectName";
import {DoorElevation} from "../../../../api/Openings";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import {Grid, IconButton} from "@mui/material";
import React, {useContext} from "react";
import {DoorElevationPopup} from "./DoorElevationPopup";
import {getAll} from "../../../logistics/routes/useGetAll";
import {useIsQuote} from "../QuoteContainer";
import {useAlternative} from "../alternative/Alternative";
import {LiteKitCreate} from "./LiteKitCreate";
import {ext} from "../../../../api/Files";
import {DropDownContext, useDropdownData} from "../../settings/DropDowns";
import {useSyncedRef} from "../../../../misc/SyncedRef";

export function ReviewDoorElevationTitle(props: {
    onDone(): void;
}) {
    return (
        <Grid container spacing={1} alignItems="center">
            <Grid item>
                <IconButton onClick={props.onDone}>
                    <ChevronLeft />
                </IconButton>
            </Grid>
            <Grid item>
                Review Door Elevations
            </Grid>
        </Grid>
    )
}

export function ReviewDoorElevations() {
    const project = useProjectId();
    const sys = useProjectUnitSystem();
    const isQuote = useIsQuote();
    const alt = useAlternative();
    const ddlCtx = useContext(DropDownContext);
    const ddlOptions = useDropdownData(ddlCtx.root);

    const ddlOptionsRef = useSyncedRef(ddlOptions);

    if(!sys) return null;

    return (
        <Table<DoorElevation>
            name={"door-elevations"}
            fetch={ctx => {
                return getAll(ctx, offset =>
                    isQuote ? api.quoteOpenings.listDoorElevations({
                        project: project,
                        alternative: alt,
                        offset,
                    }) : api.openings.listDoorElevations({
                        project: project,
                        offset,
                    })
                )
            }}
            fetchDeps={[project, alt, isQuote]}
            columns={[
                {
                    name: "Door Elevation",
                    width: 200,
                    editable: {
                        type: "custom",
                        copy: data => data.doorElevation?.toString() || "",
                        paste: (data, value) => {
                            data.prevName = data.doorElevation;
                            data.doorElevation = value;
                        },
                        render: (props) => {
                            const {onDone, ...rest} = props;
                            return <DoorElevationPopup<DoorElevation> {...rest} onDone={v => {
                                return onDone(Object.assign({}, v, {
                                    prevName: props.row.doorElevation,
                                }));
                            }} />
                        },
                        fileDropAccept: ["application/pdf", "image/*"],
                        fileDropAction: async (files, info) => {
                            const fileObj = await api.files.upload({
                                file: files[0],
                                // use guid naming to prevent collisions
                                name: window.crypto.randomUUID() + ext(files[0].name),
                                convert2pdf: true,
                            });

                            info.update(Object.assign({}, info.row, {
                                doorElevationFile: fileObj.id,
                            }));
                        },
                    },
                    editObj: (dst, src, value: string) => {
                        dst.prevName = dst.doorElevation
                        dst.doorElevation = value;
                        dst.doorElevationFile = undefined;
                    },
                    render: (data, col) => {
                        return data.doorElevation || "";
                    },
                },
                {
                    name: "Has Attachment",
                    width: 100,
                    render: data => {
                        return data.doorElevationFile ? "Yes" : "No";
                    }
                },
                {
                    name: "Lite Kit Profile",
                    width: 200,

                    editable: {
                        type: "lookup",
                        displayKey: "doorElevationProfileDefaultName",
                        getSelected: (input: DoorElevation) => {
                            if(input.doorElevationProfileDefaultName) {
                                return [input.doorElevationProfileDefaultName];
                            }

                            return [];
                        },
                        options: async (search: string, input: SelectInput<DoorElevation>) => {
                            const result = ddlOptionsRef.current.filter(c => c.category === "doorLiteKit");

                            return result.filter(r => {
                                if(!search) return true;
                                return r.display.toLowerCase().includes(search.toLowerCase());
                            })
                        },
                        freeSolo: (opts) => {
                            opts.onRender(() => <LiteKitCreate
                                onClose={opts.onClose}
                                value={opts.row}
                                createName={opts.create}
                            />)
                        },
                    },

                    editKey: "doorElevationProfileDefaultName",

                    render: data => {
                        if(data.doorElevationProfileCustomFile) return "Custom";
                        if(data.doorElevationProfileDefaultName && data.doorElevationProfileDefaultFile) return data.doorElevationProfileDefaultName
                        if(data.doorElevationProfileDefaultName) return data.doorElevationProfileDefaultName + " (no file)"
                        return "(none)"
                    }
                },
                ViewOnly(NumberCol("Qty", "count"))
            ]}
            onChange={v => {
                return isQuote ? api.quoteOpenings.updateDoorElevation(v) : api.openings.updateDoorElevation(v)
            }}
        />
    )
}